import { useEffect, useState } from "react";
import { useAuth } from "@/context/AuthContext";
import { Navigate } from "react-router-dom";
import api from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Users, Plus, Lock, Ban, RotateCcw, KeyRound } from "lucide-react";

const ADMIN_ROLES = ["superadmin", "platform_admin", "directeur", "beheerder", "locatiemanager"];

export default function GroupAccountsAdminPage() {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [locations, setLocations] = useState([]);
  const [groups, setGroups] = useState([]);
  const [showCreate, setShowCreate] = useState(false);
  const [form, setForm] = useState({ username: "", password: "", location_id: "", group_id: "", name: "" });
  const [saving, setSaving] = useState(false);
  const [resetFor, setResetFor] = useState(null); // group_account row
  const [newPassword, setNewPassword] = useState("");

  const fetchAll = async () => {
    try {
      const [ga, loc, grp] = await Promise.all([
        api.get("/group-accounts"),
        api.get("/locations"),
        api.get("/groups"),
      ]);
      setItems(ga.data?.items || []);
      setLocations(Array.isArray(loc.data) ? loc.data : loc.data?.items || []);
      setGroups(Array.isArray(grp.data) ? grp.data : grp.data?.items || []);
    } catch (e) {
      toast.error("Kon groepsaccounts niet laden");
    }
  };

  useEffect(() => {
    if (user && ADMIN_ROLES.includes(user.role)) fetchAll();
  }, [user]);

  if (user && !ADMIN_ROLES.includes(user.role)) {
    return <Navigate to="/app" replace />;
  }

  const create = async () => {
    if (!form.username || !form.password || !form.location_id || !form.group_id) {
      return toast.error("Vul alle verplichte velden in");
    }
    if (form.password.length < 8) return toast.error("Wachtwoord moet minimaal 8 tekens zijn");
    setSaving(true);
    try {
      await api.post("/group-accounts", form);
      toast.success("Groepsaccount aangemaakt");
      setShowCreate(false);
      setForm({ username: "", password: "", location_id: "", group_id: "", name: "" });
      fetchAll();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Kon niet aanmaken");
    } finally { setSaving(false); }
  };

  const block = async (gid) => {
    if (!window.confirm("Weet je zeker dat je dit groepsaccount wilt blokkeren?")) return;
    try { await api.post(`/group-accounts/${gid}/block`); toast.success("Geblokkeerd"); fetchAll(); }
    catch (e) { toast.error(e.response?.data?.detail || "Kon niet blokkeren"); }
  };
  const unblock = async (gid) => {
    try { await api.post(`/group-accounts/${gid}/unblock`); toast.success("Gedeblokkeerd"); fetchAll(); }
    catch (e) { toast.error(e.response?.data?.detail || "Kon niet deblokkeren"); }
  };
  const endSessions = async (gid) => {
    try { await api.post(`/group-accounts/${gid}/end-sessions`); toast.success("Actieve sessies beëindigd"); }
    catch (e) { toast.error(e.response?.data?.detail || "Kon sessies niet beëindigen"); }
  };
  const doReset = async () => {
    if (newPassword.length < 8) return toast.error("Wachtwoord moet minimaal 8 tekens zijn");
    try {
      await api.post(`/group-accounts/${resetFor.id}/reset-password`, { password: newPassword });
      toast.success("Wachtwoord gereset");
      setResetFor(null); setNewPassword("");
    } catch (e) { toast.error(e.response?.data?.detail || "Kon wachtwoord niet resetten"); }
  };

  const groupsForLocation = groups.filter(g => g.location_id === form.location_id);

  return (
    <div className="space-y-6" data-testid="group-accounts-admin">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-display text-2xl tracking-tight">Groepsaccounts</h1>
          <p className="text-sm text-neutral-500 mt-1">
            Gedeelde login per opvanggroep — voor dagelijkse groepstaken (presentie, activiteiten, medicatie, incidenten).
          </p>
        </div>
        <Button onClick={() => setShowCreate(true)} data-testid="create-group-account">
          <Plus className="h-4 w-4 mr-2" /> Nieuw groepsaccount
        </Button>
      </div>

      <div className="bg-white border border-neutral-200 rounded-xl overflow-hidden">
        <table className="w-full text-sm" data-testid="group-accounts-table">
          <thead className="bg-neutral-50 border-b border-neutral-200">
            <tr>
              <th className="text-left px-4 py-3 font-medium text-neutral-600">Naam</th>
              <th className="text-left px-4 py-3 font-medium text-neutral-600">Gebruikersnaam</th>
              <th className="text-left px-4 py-3 font-medium text-neutral-600">Groep · Locatie</th>
              <th className="text-left px-4 py-3 font-medium text-neutral-600">Status</th>
              <th className="text-right px-4 py-3 font-medium text-neutral-600">Acties</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-neutral-200">
            {items.length === 0 && (
              <tr><td colSpan={5} className="text-center py-8 text-neutral-500">Nog geen groepsaccounts. Klik op "Nieuw" om er één aan te maken.</td></tr>
            )}
            {items.map(r => (
              <tr key={r.id} data-testid={`group-account-row-${r.id}`}>
                <td className="px-4 py-3 font-medium">{r.name}</td>
                <td className="px-4 py-3 font-mono text-xs">{r.username}</td>
                <td className="px-4 py-3">
                  <div>{r.group_name || "—"}</div>
                  <div className="text-xs text-neutral-500">{r.location_name || "—"}</div>
                </td>
                <td className="px-4 py-3">
                  {r.blocked ? (
                    <span className="inline-flex items-center gap-1 text-xs bg-red-50 text-red-700 px-2 py-1 rounded-full">
                      <Ban className="h-3 w-3" /> Geblokkeerd
                    </span>
                  ) : (
                    <span className="inline-flex items-center gap-1 text-xs bg-emerald-50 text-emerald-700 px-2 py-1 rounded-full">
                      Actief
                    </span>
                  )}
                </td>
                <td className="px-4 py-3 text-right space-x-2">
                  <Button size="sm" variant="outline" onClick={() => setResetFor(r)} data-testid={`reset-pw-${r.id}`}>
                    <KeyRound className="h-3.5 w-3.5 mr-1" /> Reset wachtwoord
                  </Button>
                  <Button size="sm" variant="outline" onClick={() => endSessions(r.id)} data-testid={`end-sessions-${r.id}`}>
                    <RotateCcw className="h-3.5 w-3.5 mr-1" /> Sessies stoppen
                  </Button>
                  {r.blocked ? (
                    <Button size="sm" variant="outline" onClick={() => unblock(r.id)} data-testid={`unblock-${r.id}`}>Deblokkeren</Button>
                  ) : (
                    <Button size="sm" variant="outline" onClick={() => block(r.id)} data-testid={`block-${r.id}`}>
                      <Lock className="h-3.5 w-3.5 mr-1" /> Blokkeren
                    </Button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Create dialog */}
      <Dialog open={showCreate} onOpenChange={setShowCreate}>
        <DialogContent data-testid="create-dialog">
          <DialogHeader><DialogTitle>Nieuw groepsaccount</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>Locatie *</Label>
              <Select value={form.location_id} onValueChange={v => setForm(f => ({ ...f, location_id: v, group_id: "" }))}>
                <SelectTrigger data-testid="create-location"><SelectValue placeholder="Kies locatie" /></SelectTrigger>
                <SelectContent>
                  {locations.map(l => <SelectItem key={l.id} value={l.id}>{l.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Groep *</Label>
              <Select value={form.group_id} onValueChange={v => setForm(f => ({ ...f, group_id: v }))} disabled={!form.location_id}>
                <SelectTrigger data-testid="create-group"><SelectValue placeholder={form.location_id ? "Kies groep" : "Kies eerst locatie"} /></SelectTrigger>
                <SelectContent>
                  {groupsForLocation.map(g => <SelectItem key={g.id} value={g.id}>{g.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Gebruikersnaam * <span className="text-neutral-400 text-xs">(kleine letters/cijfers/./_/-)</span></Label>
              <Input value={form.username} onChange={e => setForm(f => ({ ...f, username: e.target.value.toLowerCase() }))}
                data-testid="create-username" placeholder="bv. vlinders-middenweg" />
            </div>
            <div>
              <Label>Zichtbare accountnaam <span className="text-neutral-400 text-xs">(optioneel, default = groepsnaam)</span></Label>
              <Input value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))}
                data-testid="create-display-name" placeholder="bv. Vlindergroep" />
            </div>
            <div>
              <Label>Wachtwoord * <span className="text-neutral-400 text-xs">(min 8 tekens)</span></Label>
              <Input type="password" value={form.password} onChange={e => setForm(f => ({ ...f, password: e.target.value }))}
                data-testid="create-password" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCreate(false)}>Annuleren</Button>
            <Button onClick={create} disabled={saving} data-testid="create-submit">
              {saving ? "Bezig…" : "Aanmaken"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Reset password dialog */}
      <Dialog open={!!resetFor} onOpenChange={v => !v && setResetFor(null)}>
        <DialogContent data-testid="reset-dialog">
          <DialogHeader><DialogTitle>Wachtwoord resetten — {resetFor?.name}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div className="text-sm text-neutral-600">Na resetten worden alle actieve sessies beëindigd.</div>
            <div>
              <Label>Nieuw wachtwoord (min 8 tekens)</Label>
              <Input type="password" value={newPassword} onChange={e => setNewPassword(e.target.value)}
                data-testid="reset-password-input" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setResetFor(null); setNewPassword(""); }}>Annuleren</Button>
            <Button onClick={doReset} data-testid="reset-submit">Resetten</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
